import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import { uuid } from '../../types/common-helper';
import { GameDocument, Resource } from '../../types/game-document';
import {
  ResourceEntity,
  ResourcePackEntity
} from '../../types/game-document/entities';
import { LanguageMapping } from '../../types/game-document/entities/resource-pack';
import { GetNextAssetNameAsync } from './assets';
import {
  TaskContentAnswer,
  TaskContentForm
} from '../../types/game-document/entities/task-content';
import {
  TranslateUsingOpenAIAsync,
  TranslationProgressEvent
} from '../../services/translation';

/**
 * Add resource pack.
 * @param gameDocument - The Game Document to modify
 * @param name - Name of resource pack
 * @param isCopyOriginaltext - Copy value from original resource
 * @param isTranslateUsingAI - Transalte the original resource using AI
 * @param displayLanguage - name of display language selected
 * @param displayLanguageUrl - blob file url of display language selected
 * @return The updated Game Document
 */
export const AddResourcePackAsync = async (
  gameDocument: GameDocument,
  name: string,
  isCopyOriginaltext?: boolean,
  isTranslateUsingAI?: boolean,
  displayLanguage?: string,
  displayLanguageUrl?: string,
  onProgress?: (event: TranslationProgressEvent) => void
) => {
  let resourcePacks = cloneDeep(gameDocument.resourcePacks) ?? [];
  let resourcePackName = await GetNextAssetNameAsync(resourcePacks, name);

  let originalResources: Resource[] = cloneDeep(gameDocument?.resources) ?? [];

  let newResourcePack: ResourcePackEntity = {
    id: uuid(),
    description: '',
    name: resourcePackName,
    displayLanguage: displayLanguage,
    displayLanguageUrl: displayLanguageUrl,
    resources: []
  };

  if (isTranslateUsingAI) {
    // format the resource list to follow the API requirement to done the translation
    // ignore the image and video resource type
    const resourceMap: Record<string, string> = originalResources.reduce(
      (acc, resource) => {
        if (
          resource.id !== undefined &&
          resource.type !== 'image' &&
          resource.type !== 'video'
        ) {
          acc[resource.id] = resource.value;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    // send the resources to the API for translation
    var translatedResources = await TranslateUsingOpenAIAsync(
      name,
      resourceMap,
      onProgress
    );

    // set the resources of the resource pack
    originalResources.forEach((resource) => {
      if (resource.type !== 'image' && resource.type !== 'video') {
        resource.value = translatedResources[resource.id!];
      }
      newResourcePack.resources!.push(resource as ResourceEntity);
    });
  } else {
    originalResources.forEach((resource) => {
      if (!isCopyOriginaltext) {
        resource.value = '';
      }
      newResourcePack.resources!.push(resource as ResourceEntity);
    });
  }
  resourcePacks.push(newResourcePack);

  return merge(gameDocument, {
    resourcePacks
  });
};

/**
 * Updates the resource pack's resource in the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param resourceId - The ID of the Resource to update
 * @param resource - The updated Resource
 */
export const UpdateResourcePackResourceAsync = async (
  gameDocument: GameDocument,
  name: string,
  resourceId: string,
  resource: ResourceEntity
) => {
  let resourcePacks = GetResourcePacks(gameDocument);

  let resourcePacksIndex = resourcePacks.findIndex((i) => i.name === name)!;

  if (resourcePacksIndex > -1) {
    let resourcePack = resourcePacks[resourcePacksIndex];

    let resourceIndex = resourcePack?.resources?.findIndex(
      (x) => x.id === resourceId
    )!;

    if (resourceIndex > -1) {
      resourcePacks[resourcePacksIndex].resources![resourceIndex] = resource;
    }
  }
  return merge(gameDocument, {
    resourcePacks
  });
};

/**
 * Updates the resources pack detail in the Game Document.
 * Not Update resources of resource pack.
 * @param gameDocument - The Game Document to modify
 * @param resourcePackId - The ID of the Resource pack to update
 * @param displayLanguage - name of display language selected
 * @param displayLanguageUrl - blob file url of display language selected
 * @return The updated Game Document
 */
export const UpdateResourcePackDetailAsync = async (
  gameDocument: GameDocument,
  name: string,
  resourcePackId: string,
  displayLanguage: string,
  displayLanguageUrl: string
) => {
  let resourcePacks = GetResourcePacks(gameDocument);

  let resourcePacksIndex = resourcePacks.findIndex(
    (i) => i.id === resourcePackId
  )!;

  if (resourcePacksIndex > -1) {
    let newResourcePack = resourcePacks[resourcePacksIndex];
    newResourcePack.name = name;
    newResourcePack.displayLanguage = displayLanguage;
    newResourcePack.displayLanguageUrl = displayLanguageUrl;

    resourcePacks[resourcePacksIndex] = newResourcePack;
  }
  return merge(gameDocument, {
    resourcePacks
  });
};

/**
 * Add the resource pack's resource in the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param resourceId - The ID of the Resource to update
 * @param resource - The updated Resource
 */
export const AddResourcePackResourceAsync = async (
  gameDocument: GameDocument,
  name: string,
  resource: ResourceEntity
) => {
  let resourcePacks = GetResourcePacks(gameDocument);

  let resourcePacksIndex = resourcePacks.findIndex((i) => i.name === name)!;

  if (resourcePacksIndex > -1) {
    resourcePacks[resourcePacksIndex].resources?.push(resource);

    return merge(gameDocument, {
      resourcePacks
    });
  }
};

/**
 * Retrieves all resource packs from the game document.
 * @param gameDocument - The Game Document to modify
 */
export const GetResourcePacks = (
  gameDocument: GameDocument
): ResourcePackEntity[] => {
  return (gameDocument.resourcePacks as ResourcePackEntity[]) ?? [];
};

/**
 * get resource pack by name.
 * @param gameDocument - The Game Document to modify
 * @param name - Name of resource pack
 * @return The Game Document
 */
export const GetResourcePackByName = (
  gameDocument: GameDocument,
  name: string
) => {
  const resources = cloneDeep(gameDocument?.resources);
  let newLanguageMapping: LanguageMapping[] = [];
  const selectedResourcePack = gameDocument?.resourcePacks?.find(
    (x) => x.name === name
  );

  resources?.forEach((resource) => {
    let selectedNewLanguage = selectedResourcePack?.resources?.find(
      (x) => x !== null && x.id === resource?.id
    );

    const heading = GenerateHeading(gameDocument, resource);

    newLanguageMapping.push({
      id: resource?.id,
      type: resource.type,
      originalLanguage: resource?.value,
      newLanguage: selectedNewLanguage && selectedNewLanguage?.value,
      isEditing: true,
      heading
    });
  });
  return newLanguageMapping;
};

const GenerateHeading = (
  gameDocument: GameDocument,
  resource: ResourceEntity
): string => {
  let heading = 'No Heading';
  if (gameDocument.overview) {
    if (gameDocument.overview.titleResId === resource.id) {
      heading = `Overview - Title`;
    }

    if (gameDocument.overview.longDescriptionResId === resource.id) {
      heading = `Overview - Description`;
    }

    if (gameDocument.overview.headerResId === resource.id) {
      heading = `Overview - Header`;
    }
  }

  if (gameDocument.assets.tasks) {
    gameDocument.assets.tasks?.forEach((task) => {
      if (task.titleResId === resource.id)
        heading = `Task ${task.name} - Title`;
      if (task.imageResId === resource.id)
        heading = `Task ${task.name} - Image`;
      if (task.completeImageResId === resource.id)
        heading = `Task ${task.name} - Completed Icon`;
      if (task.pin && task.pin.imageResId === resource.id)
        heading = `Task ${task.name} - Pin`;
    });
  }

  if (gameDocument.assets.taskContents) {
    gameDocument.assets.taskContents?.forEach((task) => {
      if (task.titleResId === resource.id)
        heading = `Task Content ${task.name} - Title`;

      if (task.preMessageResId === resource.id)
        heading = `Task Content ${task.name} - PreMessage`;

      if (task.contentResId === resource.id)
        heading = `Task Content ${task.name} - Intro`;

      if (task.forms) {
        task.forms.forEach((form: TaskContentForm, index: number) => {
          if (form.questionResId === resource.id)
            heading = `Task Content ${task.name} - Question Form ${index}`;

          if (form.answers) {
            form.answers.forEach((answer: TaskContentAnswer) => {
              if (answer.answerResId === resource.id)
                heading = `Task Content ${task.name} - Answer Form ${index}`;
            });
          }

          if (form.customFeedback) {
            if (form.customFeedback.allCorrectMessageResId === resource.id) {
              heading = ` Task Content ${task.name} - All Correct Message Feedback Form ${index}`;
            }

            if (form.customFeedback.allWrongMessageResId === resource.id) {
              heading = `Task Content ${task.name} - All Wrong Message Feedback Form ${index}`;
            }

            if (form.customFeedback.allWrongUrlResId === resource.id) {
              heading = ` Task Content ${task.name} - All Wrong Url Feedback Form ${index}`;
            }

            if (
              form.customFeedback.partiallyCorrectMessageResId === resource.id
            ) {
              heading = `Task Content ${task.name} - Partially Correct Message Feedback Form ${index}`;
            }

            if (form.customFeedback.partiallyCorrectUrlResId === resource.id) {
              heading = `Task Content ${task.name} - Partially Correct Url Feedback Form ${index}`;
            }
          }
        });
      }
    });
  }

  if (gameDocument.assets.items) {
    gameDocument.assets.items.forEach((item) => {
      if (item.imageResId === resource.id)
        heading = `Inventory ${item.name} - Image`;

      if (item.titleResId === resource.id)
        heading = `Inventory ${item.name} - Title`;

      if (item.summaryResId === resource.id)
        heading = `Inventory ${item.name} - Summary`;
    });
  }

  if (gameDocument.assets.items) {
    gameDocument.assets.items.forEach((item) => {
      if (item.imageResId === resource.id)
        heading = `Inventory ${item.name} - Image`;

      if (item.titleResId === resource.id)
        heading = `Inventory ${item.name} - Title`;

      if (item.summaryResId === resource.id)
        heading = `Inventory ${item.name} - Summary`;
    });
  }

  if (gameDocument.assets.titles) {
    gameDocument.assets.titles.forEach((title) => {
      if (title.imageResId === resource.id)
        heading = `Title ${title.name} - Image`;

      if (title.titleResId === resource.id)
        heading = `Title ${title.name} - Title`;

      if (title.summaryResId === resource.id)
        heading = `Title ${title.name} - Summary`;
    });
  }

  if (gameDocument.assets.timers) {
    gameDocument.assets.timers.forEach((timer) => {
      if (timer.titleResId === resource.id)
        heading = `Timer ${timer.name} - Title`;
    });
  }

  if (gameDocument.assets.areas) {
    gameDocument.assets.areas.forEach((area) => {
      if (area.imageResId === resource.id)
        heading = `Area ${area.name} - Image`;
    });
  }

  if (gameDocument.assets.zones) {
    gameDocument.assets.zones.forEach((zone) => {
      if (zone.imageResId === resource.id)
        heading = `Zone ${zone.name} - Image`;

      if (zone.titleResId === resource.id)
        heading = `Zone ${zone.name} - Title`;
    });
  }

  if (gameDocument.assets.maps) {
    gameDocument.assets.maps.forEach((map) => {
      if (map.imageResId === resource.id) heading = `Map ${map.name} - Image`;

      if (map.titleResId === resource.id) heading = `Map ${map.name} - Title`;
    });
  }

  if (gameDocument.settings) {
    if (gameDocument.settings.designer) {
      if (
        gameDocument.settings.designer.defaultAllCorrectFeedbackMessageResId ===
        resource.id
      ) {
        heading = `Setting Designer - Default All Correct Feedback Message`;
      }

      if (
        gameDocument.settings.designer.defaultAllWrongFeedbackMessageResId ===
        resource.id
      ) {
        heading = `Setting Designer - Default All Wrong Feedback Message`;
      }

      if (
        gameDocument.settings.designer.defaultFeedbackAllCorrectIconResId ===
        resource.id
      ) {
        heading = `Setting Designer - Default All Correct Feedback Icon`;
      }

      if (
        gameDocument.settings.designer.defaultFeedbackAllCorrectIconResId ===
        resource.id
      ) {
        heading = `Setting Designer - Default All Correct Feedback Icon`;
      }

      if (
        gameDocument.settings.designer.defaultFeedbackAllWrongIconResId ===
        resource.id
      ) {
        heading = `Setting Designer - Default All Wrong Feedback Icon`;
      }
    }
  }

  if (gameDocument.rules) {
    if (gameDocument.rules.worldMap) {
      if (gameDocument.rules.worldMap.imageResId == resource.id) {
        heading = `Rules - World Map - Image`;
      }

      if (gameDocument.rules.worldMap.taskImageResId == resource.id) {
        heading = `Rules - World Map - Task Image`;
      }
    }
  }

  return heading;
};

/**
 * Get resource pack's resource by name.
 * @param gameDocument - The Game Document to modify
 * @param name - Name of resource pack
 * @param resourceId - Resource Id you want to search
 * @return The Resource pack's resource
 */
export const GetResourcePackResource = (
  gameDocument: GameDocument,
  name: string,
  resourceId: string
) => {
  const selectedResourcePack = gameDocument?.resourcePacks?.find(
    (x) => x.name === name
  );

  return selectedResourcePack?.resources?.find((x) => x.id === resourceId);
};

export const FilterResourcePacks = (
  gameDocument: GameDocument,
  relevantResources: ResourceEntity[]
): ResourcePackEntity[] => {
  // Create a set of relevant resource IDs for quick lookup
  const relevantResourceIds = new Set(
    relevantResources.map((resource) => resource.id)
  );

  // Filter ResourcePacks based on whether their resources match the relevant ones
  return gameDocument.resourcePacks
    ?.map((pack) => {
      if (!pack.resources) return null;

      // Filter the resources inside the pack
      const filteredResources = pack.resources.filter((resource) =>
        relevantResourceIds.has(resource.id)
      );

      // Return a new ResourcePack with only the filtered resources
      return {
        ...pack,
        resources: filteredResources
      };
    })
    .filter((pack) => pack !== null) as ResourcePackEntity[];
};
